import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight, BookOpen, Hammer, Shield, Clock, ChevronDown, ChevronUp, Lock, Star } from 'lucide-react';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/7PZmJTxcEQ4i8xuc2HgDeB/IMAGE_REST_BLUEPRINT.png';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  return <div className="min-h-screen bg-brand-stone font-sans text-brand-dark selection:bg-brand-gold selection:text-white" data-id="element-1">
      <HeroSection data-id="element-2" />
      <HookSection data-id="element-3" />
      <ProblemSection data-id="element-4" />
      <SolutionSection data-id="element-5" />
      <DeliverablesSection data-id="element-6" />
      <TargetAudienceSection data-id="element-7" />
      <HowItWorksSection data-id="element-8" />
      <ProofSection data-id="element-9" />
      <FAQSection data-id="element-10" />
      <PricingSection data-id="element-11" />
      <Footer data-id="element-12" />
    </div>;
}
function HeroSection() {
  return <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 px-6 overflow-hidden bg-brand-darker text-white" data-id="element-13">
      <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1516962215378-7fa2e137ae93?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay" data-id="element-14"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-brand-darker/80 to-brand-darker" data-id="element-15"></div>

      <div className="relative max-w-4xl mx-auto text-center z-10" data-id="element-16">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-17">
          <span className="inline-block py-1 px-3 rounded-full bg-brand-gold/20 text-brand-gold text-sm font-semibold tracking-wider uppercase mb-6 border border-brand-gold/30" data-id="element-18">
            The Restoration Blueprint
          </span>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 text-white" data-id="element-19">
            The Master Architect Is{' '}
            <span className="text-brand-gold italic" data-id="element-20">Not Finished</span> With
            Your Story
          </h1>
          <p className="text-lg md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-21">
            A Deep-Work Biblical Framework to Reclaim Your Identity and Rebuild
            Your Future After the Fog of Loss, Failure, and Delay.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-22">
            <a href="#pricing" className="w-full sm:w-auto px-8 py-4 bg-brand-gold hover:bg-brand-goldHover text-white font-bold rounded shadow-lg shadow-brand-gold/20 transition-all flex items-center justify-center gap-2 text-lg" data-id="element-23">
              Get The Blueprint Now <ArrowRight size={20} data-id="element-24" />
            </a>
            <p className="text-sm text-gray-400 mt-4 sm:mt-0 sm:ml-4 flex items-center gap-2" data-id="element-25">
              <Lock size={14} data-id="element-26" /> Secure Checkout
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}
function HookSection() {
  return <section className="py-20 px-6 bg-brand-stone" data-id="element-27">
      <div className="max-w-3xl mx-auto" data-id="element-28">
        <FadeIn data-id="element-29">
          <p className="text-xl md:text-2xl leading-relaxed mb-6 font-serif text-brand-dark" data-id="element-30">
            <span className="text-4xl md:text-5xl font-bold text-brand-gold float-left mr-2 mt-1 leading-none" data-id="element-31">
              Y
            </span>
            ou are standing in the middle of a life you no longer recognize.
          </p>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-32">
            Perhaps it was a quiet unraveling—a dream that slowly turned to
            dust—or perhaps it was a thunderous collapse that left you gasping
            for air. At 11:00 PM, the house is silent, but your mind is loud.
          </p>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-33">
            The world has moved on, hitting milestones and celebrating "wins,"
            while you feel tethered to a past you cannot undo and a future you
            can no longer see. You've been told to "just have faith," but your
            soul feels like lead.
          </p>
          <div className="bg-white p-8 border-l-4 border-brand-gold shadow-sm my-10" data-id="element-34">
            <p className="text-xl font-serif italic text-brand-dark mb-4" data-id="element-35">
              You feel behind. You feel disqualified. You feel like the "burnt
              stones" of your life are too charred for any new foundation.
            </p>
            <p className="text-lg font-semibold text-brand-gold" data-id="element-36">
              But the God of the Bible has never been intimidated by ruins. In
              fact, He specializes in them.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}
function ProblemSection() {
  return <section className="py-20 px-6 bg-white" data-id="element-37">
      <div className="max-w-4xl mx-auto" data-id="element-38">
        <FadeIn data-id="element-39">
          <div className="text-center mb-16" data-id="element-40">
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6" data-id="element-41">
              The True Battle of Restoration
            </h2>
            <div className="w-24 h-1 bg-brand-gold mx-auto" data-id="element-42"></div>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-43">
          <FadeIn delay={0.2} data-id="element-44">
            <p className="text-lg text-gray-700 mb-6" data-id="element-45">
              The true battle isn't just about fixing your circumstances; it's
              about surviving the <strong data-id="element-46">"identity lies"</strong> that rise
              after a crisis. The enemy wants to take your season of
              disappointment and turn it into your permanent name.
            </p>
            <p className="text-lg text-gray-700 mb-6" data-id="element-47">
              You are struggling with the <strong data-id="element-48">"Timeline Lie"</strong>—the
              paralyzing fear that you are too old, too late, or too broken to
              ever be used by God again.
            </p>
            <p className="text-lg text-gray-700" data-id="element-49">
              You are exhausted from trying to be your own General Contractor,
              trying to "hustle" your way out of heartbreak, only to find
              yourself back on the floor at 2:00 AM.
            </p>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-50">
            <div className="bg-brand-stoneDark p-8 rounded-lg" data-id="element-51">
              <h3 className="font-serif text-2xl font-bold mb-6 text-brand-dark" data-id="element-52">
                Why What You Tried Didn't Work:
              </h3>
              <ul className="space-y-4" data-id="element-53">
                {["You tried to 'spiritualize' the pain by masking it with 'blessed' clichés, but hidden wounds don't heal; they fester.", "You tried to rebuild on the sand of public approval, hoping that looking 'okay' on the outside would fix the collapse on the inside.", "You looked for a finished map of your future, when God was offering you 'Daily Manna' for a one-step-at-a-time walk.", 'You focused on the walls of your life before God could fix the structural integrity of your heart.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-54">
                    <XCircle className="text-red-800 shrink-0 mt-1" size={20} data-id="element-55" />
                    <span className="text-gray-800" data-id="element-56">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
function SolutionSection() {
  return <section className="py-24 px-6 bg-brand-dark text-white overflow-hidden" data-id="element-57">
      <div className="max-w-6xl mx-auto" data-id="element-58">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-59">
          <FadeIn data-id="element-60">
            <span className="text-brand-gold font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-61">
              The Unique Mechanism
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-62">
              Introducing <br data-id="element-63" />
              <span className="text-brand-gold" data-id="element-64">The Restoration Blueprint</span>
            </h2>
            <p className="text-lg text-gray-300 mb-6 leading-relaxed" data-id="element-65">
              This is an expanded, deep-work manual designed for the "Burnt
              Stone" seasons of life. Moving beyond shallow motivation, this
              system uses the <strong data-id="element-66">"Nehemiah Framework"</strong> to guide you
              through a literal demolition of shame and a structural rebuild of
              your hope.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-67">
              We don't just patch the cracks; we re-architect your internal
              "identity software" using the unshakeable Word of God.
            </p>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-68">
            <div className="relative" data-id="element-69">
              <div className="absolute -inset-4 bg-brand-gold/20 blur-2xl rounded-full" data-id="element-70"></div>
              <img src={PRODUCT_IMAGE} alt="The Restoration Blueprint on Laptop and Mobile" className="relative z-10 w-full h-auto rounded-lg shadow-2xl border border-white/10" data-id="element-71" />
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
function DeliverablesSection() {
  const deliverables = [{
    icon: <BookOpen className="text-brand-gold" size={32} data-id="element-72" />,
    title: 'The Expanded 7-Chapter Manuscript',
    desc: "Deep biblical exposition and 'Godly Advice' for navigating a fallen, messy world."
  }, {
    icon: <Hammer className="text-brand-gold" size={32} data-id="element-73" />,
    title: 'The 15-Day Deep-Work Intensive',
    desc: "A robust daily workbook with hard-hitting reflection prompts and homework that forces you to 'dig to the rock.'"
  }, {
    icon: <Clock className="text-brand-gold" size={32} data-id="element-74" />,
    title: 'The Phase-Based Rebuild',
    desc: 'Structured demolition (Days 1–5), Foundation pouring (Days 6–10), and faithful Erection of your new life (Days 11–15).'
  }, {
    icon: <Shield className="text-brand-gold" size={32} data-id="element-75" />,
    title: 'The 2:00 AM Emergency Truth List',
    desc: 'An expanded script of spiritual warfare to silence the Accuser in the dark.'
  }, {
    icon: <Star className="text-brand-gold" size={32} data-id="element-76" />,
    title: 'Master Architect Prayers',
    desc: 'Professionally crafted prayers of surrender to shift the weight of the world off your shoulders.'
  }];
  return <section className="py-20 px-6 bg-brand-stone" data-id="element-77">
      <div className="max-w-5xl mx-auto" data-id="element-78">
        <FadeIn data-id="element-79">
          <div className="text-center mb-16" data-id="element-80">
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6 text-brand-dark" data-id="element-81">
              What You Get Inside
            </h2>
            <div className="w-24 h-1 bg-brand-gold mx-auto" data-id="element-82"></div>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-83">
          {deliverables.map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-84">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 h-full hover:shadow-md transition-shadow" data-id="element-85">
                <div className="mb-6 bg-brand-stone w-16 h-16 rounded-full flex items-center justify-center" data-id="element-86">
                  {item.icon}
                </div>
                <h3 className="font-serif text-xl font-bold mb-3 text-brand-dark" data-id="element-87">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-88">{item.desc}</p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}
function TargetAudienceSection() {
  return <section className="py-20 px-6 bg-white" data-id="element-89">
      <div className="max-w-5xl mx-auto" data-id="element-90">
        <div className="grid md:grid-cols-2 gap-12" data-id="element-91">
          <FadeIn data-id="element-92">
            <div className="bg-brand-stone p-10 rounded-2xl h-full border-t-4 border-brand-gold" data-id="element-93">
              <h3 className="font-serif text-3xl font-bold mb-8 text-brand-dark" data-id="element-94">
                Who This Is For
              </h3>
              <ul className="space-y-6" data-id="element-95">
                {['The soul rebuilding after the catastrophic end of a marriage or long-term relationship.', 'The professional standing in the debris of a failed dream or a long season of career delay.', "The person who feels 'disqualified' by a moral failure or a public mistake.", "The weary heart that feels they have 'wasted' years and can't find the strength to start over."].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-96">
                    <CheckCircle2 className="text-brand-gold shrink-0 mt-1" size={24} data-id="element-97" />
                    <span className="text-gray-800 text-lg" data-id="element-98">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-99">
            <div className="bg-gray-50 p-10 rounded-2xl h-full border-t-4 border-gray-300" data-id="element-100">
              <h3 className="font-serif text-3xl font-bold mb-8 text-gray-400" data-id="element-101">
                Who This Is NOT For
              </h3>
              <ul className="space-y-6" data-id="element-102">
                {["Those seeking a shallow 'quick fix' that ignores the need for biblical repentance and heart work.", "Individuals who want to stay in the 'comfort' of their victimhood rather than the uncertainty of their victory."].map((item, i) => <li key={i} className="flex items-start gap-4 opacity-70" data-id="element-103">
                    <XCircle className="text-gray-400 shrink-0 mt-1" size={24} data-id="element-104" />
                    <span className="text-gray-600 text-lg" data-id="element-105">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}
function HowItWorksSection() {
  const steps = [{
    num: '01',
    title: 'DEMOLITION',
    desc: "You will aggressively clear out the soul ties to 'what was' and identify the specific lies holding you captive."
  }, {
    num: '02',
    title: 'FOUNDATION',
    desc: 'You will pour the concrete of Truth, establishing a core identity that does not shift with your emotions.'
  }, {
    num: '03',
    title: 'ERECTION',
    desc: "You will begin 'Holy Movement,' reclaiming your territory and drafting the vision for the 'After' photo God is creating."
  }];
  return <section className="py-24 px-6 bg-brand-dark text-white" data-id="element-106">
      <div className="max-w-5xl mx-auto" data-id="element-107">
        <FadeIn data-id="element-108">
          <div className="text-center mb-16" data-id="element-109">
            <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6" data-id="element-110">
              How It Works
            </h2>
            <p className="text-xl text-gray-400" data-id="element-111">
              The 3-Phase Nehemiah Framework
            </p>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-112">
          {/* Connecting line for desktop */}
          <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-brand-gold/30 z-0" data-id="element-113"></div>

          {steps.map((step, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-114">
              <div className="relative z-10 flex flex-col items-center text-center" data-id="element-115">
                <div className="w-24 h-24 rounded-full bg-brand-darker border-2 border-brand-gold flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(197,160,89,0.2)]" data-id="element-116">
                  <span className="font-serif text-3xl font-bold text-brand-gold" data-id="element-117">
                    {step.num}
                  </span>
                </div>
                <h3 className="font-serif text-2xl font-bold mb-4 tracking-wider" data-id="element-118">
                  {step.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-119">{step.desc}</p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
}
function ProofSection() {
  return <section className="py-20 px-6 bg-brand-gold text-brand-darker" data-id="element-120">
      <div className="max-w-4xl mx-auto text-center" data-id="element-121">
        <FadeIn data-id="element-122">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-10" data-id="element-123">
            What Changes in 15 Days?
          </h2>

          <div className="grid md:grid-cols-2 gap-8 text-left" data-id="element-124">
            <div className="bg-white/90 backdrop-blur p-8 rounded-xl shadow-xl" data-id="element-125">
              <p className="text-lg font-medium mb-4 flex items-start gap-3" data-id="element-126">
                <ArrowRight className="shrink-0 mt-1" size={20} data-id="element-127" />
                You stop being a victim of your history and start being an
                architect of your destiny.
              </p>
              <p className="text-lg font-medium mb-4 flex items-start gap-3" data-id="element-128">
                <ArrowRight className="shrink-0 mt-1" size={20} data-id="element-129" />
                The "spirit of heaviness" is replaced by a "garment of praise."
              </p>
              <p className="text-lg font-medium flex items-start gap-3" data-id="element-130">
                <ArrowRight className="shrink-0 mt-1" size={20} data-id="element-131" />
                You stop asking "Why?" and start asking "What's next?"
              </p>
            </div>

            <div className="flex items-center justify-center p-8" data-id="element-132">
              <p className="font-serif text-2xl md:text-3xl font-bold italic leading-snug" data-id="element-133">
                "By the end of this journey, you won't just be healed; you will
                be transformed—stronger in the broken places than you were
                before the collapse."
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}
function FAQSection() {
  const faqs = [{
    q: 'How is this version different?',
    a: 'This is the Deep-Work edition. It includes expanded chapters and a work-intensive 15-day journey designed for those who need a real breakthrough, not just a light read.'
  }, {
    q: "Does this apply if I'm starting over later in life?",
    a: "Absolutely. We specifically address the 'Timeline Lie.' God restored years to Joel, and He did the same for Moses and Sarah. He can do it for you."
  }, {
    q: 'How much time does the homework take?',
    a: 'Expect to spend 20–30 minutes daily in deep meditation and reflection. This is a construction site for your soul.'
  }];
  return <section className="py-20 px-6 bg-brand-stone" data-id="element-134">
      <div className="max-w-3xl mx-auto" data-id="element-135">
        <FadeIn data-id="element-136">
          <div className="text-center mb-12" data-id="element-137">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4" data-id="element-138">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4" data-id="element-139">
            {faqs.map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-140" />)}
          </div>
        </FadeIn>
      </div>
    </section>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-141">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" data-id="element-142">
        <span className="font-serif text-lg font-bold text-brand-dark" data-id="element-143">
          {question}
        </span>
        {isOpen ? <ChevronUp className="text-brand-gold" data-id="element-144" /> : <ChevronDown className="text-gray-400" data-id="element-145" />}
      </button>
      <AnimatePresence data-id="element-146">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-147">
            <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-50 pt-4" data-id="element-148">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function PricingSection() {
  return <section id="pricing" className="py-24 px-6 bg-white" data-id="element-149">
      <div className="max-w-4xl mx-auto" data-id="element-150">
        <FadeIn data-id="element-151">
          <div className="bg-brand-dark text-white rounded-3xl overflow-hidden shadow-2xl border border-brand-gold/20" data-id="element-152">
            <div className="md:flex" data-id="element-153">
              <div className="md:w-3/5 p-10 md:p-12" data-id="element-154">
                <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4" data-id="element-155">
                  Invest In Your Restoration
                </h2>
                <p className="text-gray-300 mb-8 text-lg" data-id="element-156">
                  For the price of a single meal out, you can access the full
                  Restoration Blueprint Manuscript and the 15-Day Deep-Work
                  Intensive Toolkit today.
                </p>

                <ul className="space-y-4 mb-10" data-id="element-157">
                  {['Expanded 7-Chapter Manuscript', '15-Day Deep-Work Intensive Workbook', 'The 2:00 AM Emergency Truth List', 'Master Architect Prayers Collection'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-158">
                      <CheckCircle2 className="text-brand-gold" size={20} data-id="element-159" />
                      <span className="text-gray-200" data-id="element-160">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="md:w-2/5 bg-brand-darker p-10 md:p-12 flex flex-col justify-center items-center text-center border-t md:border-t-0 md:border-l border-white/10" data-id="element-161">
                <p className="text-gray-400 uppercase tracking-widest text-sm font-semibold mb-2" data-id="element-162">
                  Digital Access
                </p>
                <div className="flex items-start justify-center mb-6" data-id="element-163">
                  <span className="text-2xl font-bold text-brand-gold mt-2" data-id="element-164">
                    $
                  </span>
                  <span className="text-7xl font-bold text-white" data-id="element-165">9</span>
                  <span className="text-3xl font-bold text-white mt-2" data-id="element-166">
                    .99
                  </span>
                </div>

                <a href="https://www.hopemastery.com/cart/51407215001820:1" target="_blank" rel="noopener noreferrer" className="w-full py-4 bg-brand-gold hover:bg-brand-goldHover text-white font-bold rounded shadow-lg shadow-brand-gold/20 transition-all text-lg mb-4 flex items-center justify-center" data-id="element-167">
                  Download Your Blueprint Now
                </a>
                <p className="text-xs text-gray-500 flex items-center justify-center gap-1" data-id="element-168">
                  <Lock size={12} data-id="element-169" /> Secure checkout. Delivered instantly to
                  your inbox.
                </p>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}
function Footer() {
  return <footer className="bg-brand-darker py-10 text-center border-t border-white/10" data-id="element-170">
      <div className="max-w-4xl mx-auto px-6" data-id="element-171">
        <p className="font-serif text-xl text-brand-gold mb-4" data-id="element-172">
          The Restoration Blueprint
        </p>
        <p className="text-gray-500 text-sm" data-id="element-173">
          © {new Date().getFullYear()} All Rights Reserved.{' '}
          <br className="md:hidden" data-id="element-174" />
          This site is not a part of the Facebook website or Facebook Inc.
        </p>
      </div>
    </footer>;
}